import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ComplianceLogEntry,
  NoteAuditEntry,
  PersonnelNote,
} from '@src/types/personnel';
import {
  CreateVehicleNoteRequest,
  UpdateVehicleNoteRequest,
  VehicleNoteAuditRequest,
  VehiclePayerCompliance,
  VehicleRequest,
} from '@src/types/vehicle';
import { baseQuerySample } from '@store/helpers';
import { contractQueryFn } from './credentialingContract';
import { vehicleMocks } from './mocks/vehicleMocks';

const withUpperCaseVin = <T extends { vin?: unknown }>(body: T): T =>
  typeof body.vin === 'string'
    ? { ...body, vin: body.vin.trim().toUpperCase() }
    : body;

export const vehicleApi = createApi({
  reducerPath: 'Vehicle',
  tagTypes: ['Vehicle', 'VehicleNotes'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getVehicleData: builder.query({
      query: ({ page, size, sortBy = 'expirationDate', sortDir = 'desc' }) => ({
        url: `/v1/vehicles/vehicle?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    createOrUpdateVehicleData: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/vehicles/vehicle/${id ? `update/${id}` : 'create'}`,
        method,
        body: withUpperCaseVin({
          // TODO this is only for test create, need to set dynamically
          // (only fills what the form didn't send, so an entered value wins)
          ...(!id
            ? { issuedNumber: Math.floor(Math.random() * 10000) + 1 }
            : {}),
          ...(!id
            ? { plate: `ABC-${Math.floor(Math.random() * 10000) + 1}` }
            : {}),
          ...body,
        }),
      }),
    }),
    getVehicleRegistrationTableData: builder.query({
      query: ({ id, sortBy = 'expirationDate', sortDir = 'desc' }) => ({
        url: `/v1/vehicles/registration/vehicles/${id}?sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getVehicleRegistrationById: builder.query({
      query: ({ id }) => ({
        url: `/v1/vehicles/registration/${id}`,
      }),
    }),
    createOrUpdateVehicleRegistration: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/vehicles/registration/${id ? `update/${id}` : 'create'}`,
        method,
        body: withUpperCaseVin(body),
      }),
    }),
    getVehicleInspectionTableData: builder.query({
      query: ({ id, sortBy = 'expirationDate', sortDir = 'desc' }) => ({
        url: `/v1/vehicles/inspection/vehicles/${id}?sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getVehicleInspectionById: builder.query({
      query: ({ id }) => ({
        url: `/v1/vehicles/inspection/${id}`,
      }),
    }),
    createOrUpdateVehicleInspection: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/vehicles/inspection/${id ? `update/${id}` : 'create'}`,
        method,
        body: withUpperCaseVin(body),
      }),
    }),
    getVehiclePermitTableData: builder.query({
      query: ({ id, sortBy = 'expiration', sortDir = 'desc' }) => ({
        url: `/v1/vehicles/permit/vehicles/${id}?sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getVehiclePermitById: builder.query({
      query: ({ id }) => ({
        url: `/v1/vehicles/permit/${id}`,
      }),
    }),
    getVehiclePermitByVehicleId: builder.query({
      query: ({ VehicleId }: { VehicleId: number }) => ({
        url: `/v1/vehicles/permit/${VehicleId}`,
      }),
    }),
    createOrUpdateVehiclePermit: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/vehicles/permit/${id ? `update/${id}` : 'create'}`,
        method,
        body: withUpperCaseVin(body),
      }),
    }),
    getVehicleServiceDatesTableData: builder.query({
      query: ({ id, sortBy = 'expirationDate', sortDir = 'desc' }) => ({
        url: `/v1/vehicles/service-dates/vehicles/${id}?sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getVehicleServiceDatesById: builder.query({
      query: ({ id }) => ({
        url: `/v1/vehicles/service-dates/${id}`,
      }),
    }),
    getVehicleServiceDatesByDriverId: builder.query({
      query: ({ personnelId }: { personnelId: number }) => ({
        url: `/v1/vehicles/service-dates/vehicles/${personnelId}`,
      }),
    }),
    createOrUpdateVehicleServiceDates: builder.mutation({
      query: ({ id, method, body = {} }) => ({
        url: `/v1/vehicles/service-dates/${id ? `update/${id}` : 'create'}`,
        method,
        body: withUpperCaseVin(body),
      }),
    }),
    // TODO: no vehicle payer compliance endpoint yet, the path follows the
    // Personnel one; until it exists the tab shows no payers
    getVehiclePayerCompliance: builder.query<
      Array<VehiclePayerCompliance>,
      VehicleRequest
    >({
      queryFn: contractQueryFn(
        ({ vehicleId }: VehicleRequest) => ({
          url: `/v1/vehicles/vehicle/${vehicleId}/payer-compliance`,
        }),
        vehicleMocks.payerCompliance,
        { whenMissing: [] }
      ),
    }),
    // TODO: no vehicle compliance log endpoint yet, the path follows the
    // Personnel one; until it exists the log stays empty
    getVehicleComplianceLog: builder.query<
      Array<ComplianceLogEntry>,
      VehicleRequest
    >({
      queryFn: contractQueryFn(
        ({ vehicleId }: VehicleRequest) => ({
          url: `/v1/vehicles/vehicle/${vehicleId}/compliance-log`,
        }),
        vehicleMocks.complianceLog,
        { whenMissing: [] }
      ),
    }),
    // TODO: no vehicle notes endpoints yet, the paths follow the Personnel
    // ones; until they exist the notes stay empty
    getVehicleNotes: builder.query<Array<PersonnelNote>, VehicleRequest>({
      queryFn: contractQueryFn(
        ({ vehicleId }: VehicleRequest) => ({
          url: `/v1/vehicles/vehicle/${vehicleId}/notes`,
        }),
        vehicleMocks.notes,
        { whenMissing: [] }
      ),
      providesTags: (_result, _error, { vehicleId }) => [
        { type: 'VehicleNotes', id: vehicleId ?? undefined },
      ],
    }),
    createVehicleNote: builder.mutation<
      PersonnelNote,
      CreateVehicleNoteRequest
    >({
      queryFn: contractQueryFn(
        ({ note, vehicleId }: CreateVehicleNoteRequest) => ({
          url: `/v1/vehicles/vehicle/${vehicleId}/notes/create`,
          method: 'POST',
          body: note,
        }),
        vehicleMocks.createNote
      ),
      invalidatesTags: (_result, error, { vehicleId }) =>
        error ? [] : [{ type: 'VehicleNotes', id: vehicleId ?? undefined }],
    }),
    updateVehicleNote: builder.mutation<
      PersonnelNote,
      UpdateVehicleNoteRequest
    >({
      queryFn: contractQueryFn(
        ({ noteId, text, vehicleId }: UpdateVehicleNoteRequest) => ({
          url: `/v1/vehicles/vehicle/${vehicleId}/notes/update/${noteId}`,
          method: 'PUT',
          body: { text },
        }),
        vehicleMocks.updateNote
      ),
      invalidatesTags: (_result, error, { vehicleId }) =>
        error ? [] : [{ type: 'VehicleNotes', id: vehicleId ?? undefined }],
    }),
    getVehicleNoteAudit: builder.query<
      Array<NoteAuditEntry>,
      VehicleNoteAuditRequest
    >({
      queryFn: contractQueryFn(
        ({ noteId, vehicleId }: VehicleNoteAuditRequest) => ({
          url: `/v1/vehicles/vehicle/${vehicleId}/notes/${noteId}/audit`,
        }),
        vehicleMocks.noteAudit
      ),
    }),
  }),
});

export const {
  useLazyGetVehicleDataQuery,
  useCreateOrUpdateVehicleDataMutation,
  useLazyGetVehicleRegistrationTableDataQuery,
  useLazyGetVehicleRegistrationByIdQuery,
  useCreateOrUpdateVehicleRegistrationMutation,
  useLazyGetVehicleInspectionTableDataQuery,
  useLazyGetVehicleInspectionByIdQuery,
  useCreateOrUpdateVehicleInspectionMutation,
  useLazyGetVehiclePermitTableDataQuery,
  useLazyGetVehiclePermitByIdQuery,
  useCreateOrUpdateVehiclePermitMutation,
  useLazyGetVehicleServiceDatesTableDataQuery,
  useLazyGetVehicleServiceDatesByIdQuery,
  useCreateOrUpdateVehicleServiceDatesMutation,
  useLazyGetVehicleServiceDatesByDriverIdQuery,
  useLazyGetVehiclePermitByVehicleIdQuery,
  useGetVehiclePayerComplianceQuery,
  useGetVehicleComplianceLogQuery,
  useGetVehicleNotesQuery,
  useCreateVehicleNoteMutation,
  useUpdateVehicleNoteMutation,
  useLazyGetVehicleNoteAuditQuery,
} = vehicleApi;
